import { effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { Binder } from 'src/app/models/binder.model';
import { TaxDataService } from './tax-data.service';
import { UserData } from '@/models/user-data.model';
import { AppAuthService } from './auth.service';
import { ContractMathService } from './contract-math.service';
import { BinderService } from '../api/binder.service';
import { BinderSettingsService } from './binder-settings.service';
import { CalcTotals, JSONOption, masterJson } from '@/models/master-json.model';
import { ClientHybrid } from '@/models/client.model';
import { ClientService } from '../api/client.service';
import { OwnerItemsService } from '../api/owner-items.service';
import { ObjectSnapshotService } from './object-snapshot.service';
import { BinderPeople } from '@/models/sig-request.model';
import { numberToCents } from '@/utility/money-utils';


@Injectable({
  providedIn: 'root'
})


export class DocDataService {
  private profileData: UserData | null = null;
  jsonOptions: JSONOption[] = [];
  sortedClients: ClientHybrid[] = [];
  jsonData = signal<masterJson | null>(null);
  subscriptions: Subscription[] = [];
  isHigharcPlan: boolean = false;
  calcTotals!: CalcTotals;
  binder!: Binder;

  fieldList = 'mailing.address,project.name,mailing.city,mailing.state,mailing.zip,primaryName,project.address,office.LocationName,project.city,project.state,project.zip,project.consultantName,peopleList[].firstName,peopleList[].phones[].number,peopleList[].email,peopleList[].lastName,designValues.jurisdiction,designValues.parcel,designValues.powerCo,designValues.distance,designValues.waterSupply,designValues.powerLocation,designValues.septicDesign,designValues.sewerType,designValues.wetlands,financialValues.lender.name,financialValues.lender.address,financialValues.lender.officer,financialValues.lender.city,financialValues.lender.state,financialValues.lender.zip,financialValues.lender.phone,financialValues.lender.preApproveAmount,financialValues.lender.fax,financialValues.lender.depositDate'
  private objectSnapshotService = inject(ObjectSnapshotService);

  constructor(
    private appAuthService: AppAuthService,
    private contractMathService: ContractMathService,
    private currentBinderService: BinderService,
    private clientService: ClientService,
    public taxDataService: TaxDataService,
    private binderSettingsService: BinderSettingsService,
    private ownerItemsService: OwnerItemsService
  ) {
effect(() => {
  const binder = this.currentBinderService.currentBinder();
  const totals = this.currentBinderService.totalsData();
  const clients = this.clientService.clientData();
  const profile = this.appAuthService.userData();
  const ownerItems = this.ownerItemsService.ownerItems();
  if(binder && totals && clients && profile && ownerItems ) {
    this.binder = binder;
    this.calcTotals = totals;
    this.sortedClients = clients;
    this.profileData = profile;
    this.updateMasterJson();
  }
});
  }

  private round2(n: number | null | undefined): number {
    return numberToCents(n ?? 0) / 100;
  }

  updateData(newData: masterJson | null) {
    this.jsonData.set(newData);
  }

  updateSelectedEmail(clientId: number, email: string, displayName: string) {
    console.log('updating selected email:', clientId, email, displayName);
    let currentData = this.jsonData();
    if (currentData) {
      const updatedClients = currentData.peopleList.map(client => {
        if (client.clientId === clientId) {
          return { ...client, email: email, name: displayName };
        }
        // Create a new object to ensure immutability
        return { ...client };
      });
      let updatedData = { ...currentData, peopleList: updatedClients };
      this.jsonData.set(updatedData);
    }
  }

  updateSelectedMode(clientId: number, mode: string) {
    console.log('updating selected mode:', clientId, mode);
    let currentData = this.jsonData();
    if (currentData) {
      const updatedClients = currentData.peopleList.map(client => {
        if (client.clientId === clientId) {
          console.log('updating mode:', clientId, mode);
          return { ...client, mode: mode };
        }
        console.log('client not updated:', client);
        // Create a new object to ensure immutability
        return { ...client };
      });
      console.log('updatedClients:', updatedClients);
      let updatedData = { ...currentData, peopleList: updatedClients };
      this.jsonData.set(updatedData);
      console.log('updatedData:', updatedData);
      const inPerson = this.jsonData()?.peopleList.some(client => client.mode === 'in-person');
      console.log('inPerson:', inPerson);
    }
  }

  updateMasterJson() {
    this.updateJsonObject(this.calcTotals, this.jsonOptions).subscribe(json => {
      if (json) {
        this.jsonData.set(json);
        const snapshot = this.objectSnapshotService.getSnapshotHash(json, this.fieldList);
      }
    });

  }

  private generatePeopleList(): BinderPeople[] {
    // Determine whether to use 'Contacts' or 'Clients' based on your conditions
    const peopleSource = this.sortedClients;
    // Map the source to the required format, generating 'id' and 'order' on the fly
    const peopleList = peopleSource.map((person, index) => {
      const phones = person.phones?.map(phone => ({ int: phone, number: phone }));
      return {
        email: person.Email || '',
        firstName: person.ClientFirstName || '',
        lastName: person.ClientLastName || '',
        clientId: typeof person.clientId === 'string' ? Number(person.clientId) : (person.clientId || 0),
        name: `${person.ClientFirstName} ${person.ClientLastName}`,
        displayName: person.ClientFirstName + " " + person.ClientLastName,
        mailing_address: person.mailing_address,
        mode: person.mode || 'email',
        id: index + 1,
        order: index + 1,
        phones: phones,
        request: false,
        role: "client"
      };
    }).filter((person): person is BinderPeople => person !== null);


    this.addNonClientPeople(peopleList);

    return peopleList;
  }

  private addNonClientPeople(peopleList: BinderPeople[]) {
    // Example of adding non-client people, adjust as necessary
    const nonClientPeople = [
      {
        email: '',
        firstName: "Jane",
        lastName: "Doe",
        name: "Contractor",
        displayName: "Contractor",
        id: peopleList.length + 1,
        order: peopleList.length + 1,
        phones: [],
        request: false,
        role: "contractor",
        clientId: 1,
        mailing_address: '',
        mode: 'email',
        deliver_email: true
      },
      {
        email: 'corporate@lexarhomes.com',
        firstName: "",
        lastName: "Lexar Homes, LLC",
        name: "James Moen",
        displayName: "Lexar Homes, LLC",
        id: peopleList.length + 2,
        order: peopleList.length + 2,
        phones: [],
        request: false,
        role: "corp",
        clientId: 0,
        mailing_address: '',
        mode: 'email',
        deliver_email: true
      },
    ];

    // Append non-client people to the peopleList
    peopleList.push(...nonClientPeople);
  }

  combineNames(peopleList: any[]) {
    // Filter the list to include only clients
    const clients = peopleList.filter(person => person.role === "client");

    // Define the accumulator's type directly in the reduce function
    const lastNameGroups = clients.reduce<{ [key: string]: string[] }>((acc, client) => {
      const lastName = client.lastName; // Adjusted to match the peopleList object structure
      const firstName = client.firstName; // Adjusted to match the peopleList object structure
      if (!acc[lastName]) {
        acc[lastName] = [];
      }
      acc[lastName].push(firstName);
      return acc;
    }, {});

    const combinedNames = Object.entries(lastNameGroups).map(([lastName, firstNames]) => {
      if (firstNames.length > 1) {
        return `${firstNames.join(', ')} ${lastName}`;
      } else {
        return `${firstNames[0]} ${lastName}`;
      }
    });

    return combinedNames.join(', ');
  }

  private sortAndCalculateAssembliesTotals(): any[] {
    let higharc = false;
    if (!this.binder || !this.binder.BinderAssemblies) {
      console.error('Binder or BinderAssemblies is undefined');
      return [];
    }

    if(this.binder.haId) {
      higharc = true;
    }

    // console.log('higharc check:',higharc);
    // console.log('higharc options:',this.binder.BinderAssemblies);

    // Clone the assemblies array to avoid modifying the original
    const assembliesClone = this.binder.BinderAssemblies.map(assembly => ({
      
      categoryName: higharc && assembly.Type === 'O' ? assembly.Name : assembly.Categories.Name,
      description: assembly.Description,
      quantity: typeof assembly.Quantity === 'string' ? Number(assembly.Quantity) : (assembly.Quantity || 0),
      total: this.round2((assembly.Quantity || 0) * (assembly.Price || 0)),
      showPrice: this.binderSettingsService.pdfPricing() && (  assembly.Price !== null && assembly.Price > 0),
      showIncluded: this.binderSettingsService.showIncluding(),
      type: assembly.Type,
      CategoryID: assembly.CategoryID,
      PrintOrder: assembly.PrintOrder,
      Categories: assembly.Categories,
      hidden: assembly.hidden
    }));



    assembliesClone.sort((a, b) => {
      if (a.CategoryID === b.CategoryID) {
        return a.PrintOrder - b.PrintOrder;
      }
      return a.CategoryID - b.CategoryID;
    });

    // Set categoryName for the first assembly in each category
    let currentCategoryID: number | null = null;
    if(higharc) {
      assembliesClone.forEach((assembly, index) => {
        if (assembly.CategoryID !== currentCategoryID) {
          currentCategoryID = assembly.CategoryID;
          assembly.categoryName = assembly.Categories.Name;
        } else if(assembly.type !== 'O') {
          assembly.categoryName = '';
        }
      });
    } else {
      assembliesClone.forEach((assembly, index) => {
        if (assembly.CategoryID !== currentCategoryID ) {
          currentCategoryID = assembly.CategoryID;
          assembly.categoryName = assembly.Categories.Name;
        } else {
          assembly.categoryName = '';
        }
      });
    }

    return assembliesClone;
  }

  generateProjectNumber(): string {
    let number = this.binder.JobNumber?.toString()
    if(!number) {
      const prefix = this.binder.Franchises_Binders_MainFIDToFranchises.Code
      const suffix = (this.binder.BinderID).toString().padStart(6, '0');
      number = `${prefix}${suffix}`
    }
    return number
  }

  getTax() {
    return this.taxDataService.taxData()
  }

  convertToPercent(value: number | null): string {
    if (value) {
        const percentValue = value * 100;
        return percentValue.toLocaleString('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2
        });
    }
    return '0.0';
}

  getTaxRate() {
    if(this.binder.TaxOverride) {
      return this.binder.TaxRate || 0
    }
    return this.binder.TaxRate || 0
  }

  updateJsonObject(totals: CalcTotals, higharcOptions: JSONOption[]): Observable<masterJson | undefined> {
    if (totals) {
      const isHigharc = this.binder.haId ? true : false;
      const isFounders = this.binder.Plans.Collection==='Founders'
      const ownerItems = this.ownerItemsService.ownerItems().map(item => ({
        Item: item.Item,
        Priceint: item.Priceint
      }));
      const peopleList = this.generatePeopleList();
      const assemblies = this.sortAndCalculateAssembliesTotals();
      const projectNumber = this.generateProjectNumber();
      const date = new Date().toLocaleDateString('en-us', { year: "numeric", month: "long", day: "numeric" });
      const taxSavingsTotal = this.round2(
        totals.savings.reductions.filter(item => item.taxSaving).reduce((acc, item) => acc + item.total, 0)
      );
      const mailingAddress = this.binder.contacts?.find(contact => contact.primary)?.properties.mailing_address || '';

      const property = this.binder.PropertyValue || 0;
      const oTotal = ownerItems.reduce((acc, item) => acc + item.Priceint, 0)
      const grandTotal = this.round2((totals.contractTotal || 0) + (property || 0) + (oTotal || 0));

      const designTotalC = numberToCents(totals.design.designTotal);
      const firstFeeC = Math.round(designTotalC * 0.5);
      const remainderFeeC = designTotalC - firstFeeC;
      const firstFee = firstFeeC / 100;
      const remainderFee = remainderFeeC / 100;

      const consultantName = (this.profileData?.isDev && this.binder.Users?.DisplayName) || this.profileData?.DisplayName || '';


      const primaryName = peopleList[0].name;

      const planPrice = this.binder.ShowTravel
        ? this.round2(this.binder.PlanPrice || 0)
        : this.round2((this.binder.PlanPrice || 0) + (this.binder.Travel || 0));

      const jsonObject: masterJson = {
        BinderID: this.binder.BinderID,
        assemblies: assemblies.filter(assembly => assembly.hidden !== true),
        higharch: isHigharc,
        shortDate: new Date().toLocaleDateString('en-us', { year: "numeric", month: "short", day: "numeric" }),
        combinedNames: this.combineNames(peopleList),
        higharcOptions: higharcOptions,
        warranty: isHigharc || isFounders ? 'Founders' : 'Signature',
        date: date,
        primaryName: primaryName,
        designValues: {
          exposure: this.binder.Exposure || '',
          frost: this.binder.FrostDepth || 0,
          jurisdiction: this.binder.County || '',
          parcel: this.binder.SitePlot || '',
          seismic: this.binder.Seismic || 0,
          powerCo: this.binder.powerCo || '',
          powerLocation: this.binder.powerLoc || '',
          waterSupply: this.binder.waterSupply || '',
          septicDesign: this.binder.septicDesigned ? 'Yes' : 'No',
          sewerType: this.binder.septic ? 'Septic' : 'Sewer',
          wetlands: this.binder.wetlands ? 'Yes' : 'No',
          snowLoad: {
            ground: this.binder.snowType === 'Ground' ? this.binder.snowload || 0 : 0,
            roof: this.binder.snowType === 'Roof' ? this.binder.snowload || 0 : 0
          },
          soil: this.binder.SoilPSF || 0,
          windLoad: {
            ASD: this.binder.windType === 'ASD' ? this.binder.windload || 0 : 0,
            ULT: this.binder.windType === 'ULT' ? this.binder.windload || 0 : 0
          }
        },
        project: {
          address: this.binder.SiteAddress || '',
          city: this.binder.SiteCity || '',
          consultantName: consultantName,
          name: this.binder.ClientLastName + '-' + projectNumber,
          state: this.binder.SiteState || '',
          zip: this.binder.SiteZip || 0
        },
        plan: {
          planName: this.binder.Plans.PlanName || '',
          orientation: this.binder.Orientation || '',
        },
        financialValues: {
          contractBalance: this.round2(totals.balance),
          credit: {
            amount: this.round2(this.binder.Credit || 0),
            title: this.binder.CreditDesc || ''
          },
          designServiceFee: {
            price: this.round2(totals.design.designTotal),
            designFeeFirst: this.round2(firstFee),
            designeFeeRemainder: this.round2(remainderFee),
            standardDesign: this.round2(totals.design.initialDesign),
            addEngineering: this.round2(totals.options.engineeringTotal)
          },
          dispersalBalance: this.round2(totals.dispersalBalance),
          draws: this.contractMathService.parseDraws(this.binder, totals),
          dueLessTax: this.round2(totals.dueLessTax),
          engineeringFee: this.round2(totals.options.engineeringTotal),
          lender: {
            address: "",
            city: "",
            depositeDate: "",
            disableClosing: false,
            fax: "",
            isLender: !this.binder.Lenders?.isCash || false,
            name: this.binder.Lenders?.Title || '',
            officer: "",
            phone: "",
            preapprovedAmount: this.binder.preapprovedAmount || 0,
            state: ""
          },
          optionsTotal: this.round2(totals.options.optionTotal),
          planPrice: this.round2(planPrice || 0),
          processingFee: this.round2(this.binder.Processing_Fee || 0),
          projectCost: this.round2(totals.contractTotal || 0),
          showTravel: this.binder.ShowTravel || false,
          subtotal: this.round2(totals.subtotal),
          taxSavingsTable: totals.savings.reductions,
          taxValues: {
            code: this.binder.Tax_Location || 0,
            name: this.binder.County || '',
            rate: this.binder.TaxOverride ? (this.binder.TaxRate || 0) + "%" : this.convertToPercent(this.getTax().rate/100) + "%",
            totalAmount: this.round2(totals.tax),
            finalTax: this.round2((totals.tax || 0) - (taxSavingsTotal || 0))
          },
          travelRate: this.round2(this.binder.Travel || 0),
          travelTitle: "Travel Fee",
        },
        mailing: {
          mailing_address: mailingAddress,
          address: this.binder.Clients?.MailingAddress || '',
          city: this.binder.Clients?.MailingCity || '',
          state: this.binder.Clients?.MailingState || '',
          zip: this.binder.Clients?.MailingZip || ''
        },
        notes: [],
        oItems: {
          items: ownerItems,
          itemTotal: oTotal,
          property: property,
          grandTotal: grandTotal
          },
        office: {
          ConstructionCo: this.binder.Franchises_Binders_SelectedFIDToFranchises.ConstructionCo || '',
          LDate: this.binder.Franchises_Binders_SelectedFIDToFranchises.LDate || null,
          LNumber: this.binder.Franchises_Binders_SelectedFIDToFranchises.LNumber || '',
          LocationAddress: this.binder.Franchises_Binders_SelectedFIDToFranchises.LocationAddress || '',
          LocationCity: this.binder.Franchises_Binders_SelectedFIDToFranchises.LocationCity,
          LocationName: "Lexar Homes of " + this.binder.Franchises_Binders_SelectedFIDToFranchises.Title,
          LocationState: this.binder.Franchises_Binders_SelectedFIDToFranchises.LocationState,
          LocationZip: this.binder.Franchises_Binders_SelectedFIDToFranchises.LocationZip,
          daysToFinish: this.binder.Franchises_Binders_SelectedFIDToFranchises.ConstructionDays,
          daysToStart: this.binder.Franchises_Binders_SelectedFIDToFranchises.ConStartDays
        },
        peopleList: peopleList
      };
      console.log('jsonObject:', jsonObject);
      return of(jsonObject);
    } else {
      return of(undefined);
    }
  }


}
